import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import {
  AssignmentLockConflictError,
  formatAssignmentLockMessage,
} from '../../../lib/caseAssignmentLock';

/**
 * AssignUserModal
 *
 * Modal réutilisable pour assigner un utilisateur (filtré par rôle) à un cas ou une phase.
 *
 * Props:
 *  - isOpen, onClose
 *  - onAssign(userId, fullName, { force }) => Promise<void>
 *  - roleType : string | string[]
 *  - title, description
 *  - allowForce : boolean — affiche la case « Forcer » (administrateur)
 *  - assignmentContext : 'initial_review' | 'workshop' | undefined — info contexte (réservé)
 */
const AssignUserModal = ({
  isOpen,
  onClose,
  onAssign,
  roleType,
  title = 'Assigner un utilisateur',
  description = 'Sélectionnez un utilisateur à assigner à ce cas.',
  allowForce = false,
  assignmentContext,
}) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [lockConflict, setLockConflict] = useState(null);
  const [forceAssign, setForceAssign] = useState(false);
  const overlayRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    setSelectedUserId(null);
    setSearch('');
    setSuccessMessage('');
    setErrorMessage('');
    setLockConflict(null);
    setForceAssign(false);

    const fetchUsers = async () => {
      setIsLoading(true);
      setErrorMessage('');

      try {
        const roles = Array.isArray(roleType) ? roleType : [roleType];

        const { data: matchingRoles, error: rolesError } = await supabase
          .from('user_roles')
          .select('id, role_type, role_name')
          .in('role_type', roles);

        if (rolesError) throw rolesError;

        if (!matchingRoles || matchingRoles.length === 0) {
          setErrorMessage(`Le rôle "${roleType}" n'existe pas dans le système.`);
          setUsers([]);
          setFilteredUsers([]);
          setIsLoading(false);
          return;
        }

        const roleIds = matchingRoles.map((r) => r.id);
        const roleMap = Object.fromEntries(matchingRoles.map((r) => [r.id, r]));

        const { data: assignments, error: assignError } = await supabase
          .from('user_role_assignments')
          .select('user_id, role_id')
          .eq('is_active', true)
          .in('role_id', roleIds);

        if (assignError) throw assignError;

        if (!assignments || assignments.length === 0) {
          setErrorMessage(`Aucun utilisateur actif trouvé avec le rôle "${roleType}".`);
          setUsers([]);
          setFilteredUsers([]);
          setIsLoading(false);
          return;
        }

        const userRoleMap = new Map();
        assignments.forEach((a) => {
          if (!userRoleMap.has(a.user_id)) {
            userRoleMap.set(a.user_id, roleMap[a.role_id]);
          }
        });

        const userIds = Array.from(userRoleMap.keys());

        const { data: profiles, error: profilesError } = await supabase
          .from('user_profiles')
          .select('id, full_name, email')
          .in('id', userIds);

        if (profilesError) throw profilesError;

        const list = (profiles || []).map((p) => {
          const role = userRoleMap.get(p.id);
          return {
            id: p.id,
            fullName: p.full_name || 'Sans nom',
            email: p.email || '',
            avatarUrl: `https://ui-avatars.com/api/?name=${encodeURIComponent(p.full_name || 'U')}&background=6366f1&color=fff`,
            roleName: role?.role_name || role?.role_type || '',
          };
        });

        setUsers(list);
        setFilteredUsers(list);
      } catch (err) {
        console.error('Error fetching users for assignment:', err);
        setErrorMessage(`Impossible de charger la liste des utilisateurs: ${err.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, [isOpen, roleType]);

  useEffect(() => {
    if (!search.trim()) {
      setFilteredUsers(users);
      return;
    }
    const q = search.toLowerCase();
    setFilteredUsers(
      users.filter(
        (u) =>
          u.fullName.toLowerCase().includes(q) ||
          u.email.toLowerCase().includes(q)
      )
    );
  }, [search, users]);

  useEffect(() => {
    if (isOpen && searchRef.current) {
      setTimeout(() => searchRef.current?.focus(), 150);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  const handleAssign = async () => {
    if (!selectedUserId) return;
    setIsAssigning(true);
    setErrorMessage('');
    setLockConflict(null);

    try {
      const assignedUser = users.find((u) => u.id === selectedUserId);
      await onAssign(selectedUserId, assignedUser?.fullName || null, {
        force: allowForce && forceAssign,
        assignmentContext,
      });

      setSuccessMessage(
        `${assignedUser?.fullName || 'Utilisateur'} a été assigné(e) avec succès.`
      );
      setTimeout(() => {
        onClose();
      }, 1800);
    } catch (err) {
      console.error('Assignment error:', err);
      if (err instanceof AssignmentLockConflictError) {
        setLockConflict(err.activeAssignment);
        setErrorMessage(err.message || formatAssignmentLockMessage(err.activeAssignment));
      } else {
        setErrorMessage(`Erreur lors de l'assignation: ${err.message}`);
      }
    } finally {
      setIsAssigning(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200"
    >
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h2 className="text-lg font-semibold text-foreground">{title}</h2>
            <p className="text-sm text-muted-foreground mt-0.5">{description}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {successMessage && (
          <div className="mx-6 mt-4 flex items-center gap-3 rounded-xl bg-success/10 border border-success/20 px-4 py-3 text-sm text-success">
            <Icon name="CheckCircle2" size={20} className="flex-shrink-0" />
            <span>{successMessage}</span>
          </div>
        )}

        {errorMessage && !successMessage && (
          <div className="mx-6 mt-4 rounded-xl bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive space-y-2">
            <div className="flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="flex-shrink-0 mt-0.5" />
              <span>{errorMessage}</span>
            </div>
            {lockConflict?.caseId && (
              <button
                type="button"
                onClick={() => navigate(`/case-detail/${lockConflict.caseId}`)}
                className="ml-8 text-xs font-medium text-primary hover:underline"
              >
                Voir la commande active ({lockConflict.caseNumber || lockConflict.caseId})
              </button>
            )}
          </div>
        )}

        {allowForce && lockConflict && !successMessage && (
          <div className="mx-6 mt-3 flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/5 px-3 py-2.5">
            <input
              id="force-assign-checkbox"
              type="checkbox"
              checked={forceAssign}
              onChange={(e) => setForceAssign(e.target.checked)}
              className="mt-0.5 rounded border-border"
            />
            <label htmlFor="force-assign-checkbox" className="text-xs text-foreground cursor-pointer">
              Forcer l&apos;assignation (administrateur) — l&apos;opérateur conservera aussi sa commande
              précédente jusqu&apos;à clôture de phase.
            </label>
          </div>
        )}

        {!successMessage && (
          <div className="px-6 pt-4">
            <div className="relative">
              <Icon
                name="Search"
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
              />
              <input
                ref={searchRef}
                type="text"
                placeholder="Rechercher par nom ou email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-lg border border-border bg-background pl-9 pr-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all"
              />
            </div>
          </div>
        )}

        {!successMessage && (
          <div className="flex-1 overflow-y-auto px-6 py-3 min-h-0">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-12 gap-3">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
                <span className="text-sm text-muted-foreground">Chargement...</span>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 gap-2 text-muted-foreground">
                <Icon name="UserX" size={32} />
                <span className="text-sm text-center">
                  {users.length === 0
                    ? errorMessage || 'Aucun utilisateur trouvé pour ce rôle.'
                    : 'Aucun résultat pour cette recherche.'}
                </span>
              </div>
            ) : (
              <ul className="space-y-1">
                {filteredUsers.map((user) => {
                  const isActive = selectedUserId === user.id;
                  return (
                    <li key={user.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedUserId(user.id)}
                        className={`w-full flex items-center gap-3 rounded-xl px-3 py-3 text-left transition-all duration-150 ${
                          isActive
                            ? 'bg-primary/10 border border-primary/30 shadow-sm'
                            : 'hover:bg-muted border border-transparent'
                        }`}
                      >
                        <div
                          className="w-10 h-10 rounded-full overflow-hidden bg-muted flex-shrink-0 ring-2 ring-offset-1 ring-offset-card transition-all duration-150"
                          style={{
                            ringColor: isActive ? 'var(--primary)' : 'transparent',
                          }}
                        >
                          <Image
                            src={user.avatarUrl}
                            alt={`Avatar de ${user.fullName}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${isActive ? 'text-primary' : 'text-foreground'}`}>
                            {user.fullName}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {user.email}
                          </p>
                        </div>
                        <span className="text-[11px] px-2 py-0.5 rounded-md bg-muted text-muted-foreground whitespace-nowrap">
                          {user.roleName}
                        </span>
                        {isActive && (
                          <Icon name="CheckCircle2" size={18} className="text-primary flex-shrink-0" />
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {!successMessage && (
          <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-border bg-muted/30">
            <span className="text-xs text-muted-foreground">
              {filteredUsers.length} utilisateur{filteredUsers.length !== 1 ? 's' : ''}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={onClose}>
                Annuler
              </Button>
              <Button
                variant="default"
                size="sm"
                disabled={!selectedUserId || isAssigning}
                onClick={handleAssign}
              >
                {isAssigning ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                    Assignation...
                  </>
                ) : forceAssign && allowForce ? (
                  'Forcer l\'assignation'
                ) : (
                  'Assigner'
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssignUserModal;
